import { Divider } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { FormikContextTypeEnhanced } from '@superdispatch/forms';
import {
  ColorDynamic,
  Column,
  Columns,
  Inline,
  Stack,
  useResponsiveValue,
} from '@superdispatch/ui';
import { Box, Button } from '@superdispatch/ui-lab';
import { Form, FormikProvider, FormikValues } from 'formik';
import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { isWebView } from 'shared/constants/AppTypeConstants';
import { MobileAppBridge } from 'shared/data/MobileAppBridge';
import { PageHeader, PageLayout } from 'shared/layout/PageLayout';
import { WebviewLayout } from 'shared/layout/WebviewLayout';
import { Prompt } from 'shared/routing/NavigationBlock';
import { BackButton } from 'shared/ui/BackButton';
import styled, { css } from 'styled-components';
import { viewportHeight } from 'shared/layout/NavigationHeaderBannerHeight';

const nativeContainerStyles = css`
  flex: 1;
  min-height: 100%;
  height: auto;
`;

const Container = styled.div<{ $isNative: boolean }>`
  display: flex;
  flex-direction: column;
  height: ${viewportHeight()};
  ${({ $isNative }) => $isNative && nativeContainerStyles}
`;

const Content = styled.div`
  overflow: auto;
  padding: 16px;

  [aria-hidden='true'] & {
    overflow: visible;
  }
`;

const StickyActions = styled.div`
  margin-top: auto;
`;

interface ProfilePageLayoutProps {
  title: string;
  formik?: FormikContextTypeEnhanced<FormikValues, unknown>;

  isLoading?: boolean;
  size?: 'small' | 'medium' | 'large';

  actions?: ReactNode;
  endActions?: ReactNode;
  extraDetails?: ReactNode;
  children: ReactNode;
}

export function ProfilePageLayout({
  title,
  formik,
  children,
  isLoading,
  actions,
  endActions,
  extraDetails,
  size = 'small',
}: ProfilePageLayoutProps) {
  const isMobile = useResponsiveValue(true, false);
  const navigate = useNavigate();
  const isNative = MobileAppBridge.isInjected();

  const isSubmitted = formik?.status.type === 'submitted';
  const isSubmitDisabled = !formik?.dirty;

  const headerFormAction = actions ? (
    actions
  ) : formik && actions !== false ? (
    <Button
      type="button"
      pending={formik.isSubmitting}
      disabled={isSubmitDisabled}
      fullWidth={isWebView}
      onClick={() => {
        void formik.submitForm();
      }}
    >
      Save
    </Button>
  ) : null;

  return (
    <ProfilePageUniversalLayoutWrapper
      title={
        isWebView ? (
          title
        ) : (
          <Inline verticalAlign="center">
            {isMobile && (
              <BackButton
                onClick={() => {
                  navigate(-1);
                }}
              />
            )}
            {title}
          </Inline>
        )
      }
      formik={formik}
      isLoading={isLoading}
      size={size}
      actions={headerFormAction}
      endActions={endActions}
    >
      <Prompt
        when={!!formik && !isSubmitted && (formik.dirty || formik.isSubmitting)}
        message="Changes have not been saved. Leaving can result in unsaved changes being lost."
      />
      <ProfilePageLayoutFormWrapper
        formik={formik}
        title={title}
        isLoading={isLoading}
      >
        <Box
          marginLeft="auto"
          marginRight="auto"
          maxWidth={
            size === 'large' ? '1110px' : size === 'medium' ? '992px' : '496px'
          }
          paddingLeft={size !== 'small' ? 'large' : undefined}
          paddingRight={size !== 'small' ? 'large' : undefined}
        >
          {isLoading ? (
            <div aria-label={`Loading ${title}`}>
              <Skeleton width={170} height={24} />
              <Skeleton width={270} height={24} />
            </div>
          ) : extraDetails ? (
            isNative ? (
              <Stack space="large">
                <Box paddingTop="small">
                  <Stack align="right" space="large">
                    {children}
                  </Stack>
                </Box>
                {extraDetails}
              </Stack>
            ) : (
              <Columns space="xxlarge" collapseBelow="desktop">
                <Column>
                  <Box
                    maxWidth="496px"
                    paddingTop={isWebView ? 'small' : 'medium'}
                  >
                    <Stack align="right" space="large">
                      {children}
                      {!isWebView && headerFormAction}
                    </Stack>
                  </Box>
                </Column>
                <Column>{extraDetails}</Column>
              </Columns>
            )
          ) : (
            <Stack align="right" space="large">
              {children}
              {!isWebView && headerFormAction}
            </Stack>
          )}
        </Box>
      </ProfilePageLayoutFormWrapper>
    </ProfilePageUniversalLayoutWrapper>
  );
}

function ProfilePageLayoutFormWrapper({
  title,
  formik,
  isLoading,
  children,
}: {
  title: string;
  formik?: FormikContextTypeEnhanced<FormikValues, unknown>;
  isLoading?: boolean;
  children: ReactNode;
}) {
  // eslint-disable-next-line react/jsx-no-useless-fragment
  if (!formik) return <>{children}</>;

  return (
    <FormikProvider value={formik}>
      <Form aria-label={`${title} form`} aria-busy={isLoading}>
        {children}
      </Form>
    </FormikProvider>
  );
}

type ProfilePageLayoutWrapperProps = Omit<ProfilePageLayoutProps, 'title'> & {
  title: ReactNode;
};

function ProfilePageUniversalLayoutWrapper({
  title,
  children,
  isLoading,
  size,
  actions,
  endActions,
}: ProfilePageLayoutWrapperProps) {
  const isNative = MobileAppBridge.isInjected();

  if (isWebView) {
    return (
      <WebviewLayout scrollable={false} backgroundColor={ColorDynamic.White}>
        <Container $isNative={isNative}>
          <Content>{children}</Content>
          <StickyActions>
            <Divider />
            <Box padding="small">{actions}</Box>
          </StickyActions>
        </Container>
      </WebviewLayout>
    );
  }

  return (
    <PageLayout
      background="White"
      loading={isLoading}
      stickyHeader={true}
      disablePaddings={size !== 'small'}
      header={<PageHeader title={title} endActions={endActions} />}
    >
      {children}
    </PageLayout>
  );
}
